import { Box, Button } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import styles from './OpeningStatement.module.scss';
import classNames from 'classnames/bind';
import { PlusIcon, TrashIcon } from '~/assets/icons';
import { BaseHeaderTitle } from '~/components/common';
import { useTranslation } from 'react-i18next';
import { catchError, handleApiError, notify } from '~/utils/helper';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { templateActions } from '~/thunks/template/templateSlice';
import { handleGetTemplate, handleGetTemplates } from '~/thunks/template/templateThunk';
import usePermission from '~/hooks/usePermission';
import { PERMISSIONS } from '~/utils/constants/permission';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import EditOpeningStatementDialog from '~/components/templates/opening-statement/edit-opening-statement-dialog/EditOpeningStatementDialog';
import { ITemplate, IPayloadDeleteTemplates } from '~/utils/interface/template';
import ModalAlertDelete from '~/components/common/modal-alert-delete/ModalAlertDelete';
import templateApi from '~/apis/template';
import OpeningStatementList from '~/components/templates/opening-statement/list-term-condition/OpeningStatementList';

const cx = classNames.bind(styles);

const TemplateOpeningStatement = () => {
  const dispatch = useAppDispatch();
  const permissions = usePermission();
  const { t } = useTranslation('admin/template');
  const [editData, setEditData] = useState<ITemplate>();
  const [openEditTemplateDialog, setOpenEditTemplateDialog] = useState<boolean>(false);
  const [editTemplateDialogMode, setEditTemplateDialogMode] = useState<string>('');
  const { filter, selectedIds, needRefreshData, openAlertDelete, menuSelectedId } = useAppSelector(
    (state) => state.template
  );

  useEffect(() => {
    dispatch(handleGetTemplates({ ...filter, type: 'openingStatement' }))
      .unwrap()
      .catch((e) => {
        handleApiError(e);
      });
  }, [dispatch, filter, needRefreshData]);

  const openAlertDeleteModal = useCallback(() => {
    dispatch(templateActions.changeOpenAlertDelete(true));
  }, [dispatch]);

  const handleClickTemplate = async () => {
    setEditTemplateDialogMode(DIALOG_MODE_CREATE);
    setOpenEditTemplateDialog(true);
  };

  const handleSetOpen = async (open: boolean, needRefreshData?: boolean) => {
    setOpenEditTemplateDialog(open);
    if (needRefreshData) dispatch(templateActions.changeNeedRefreshData(true));
  };

  const handleOpenEdit = async (id: string) => {
    try {
      const response = await dispatch(handleGetTemplate({ id: id.toString(), type: 'openingStatement' })).unwrap();
      setEditData(response);
      setEditTemplateDialogMode(DIALOG_MODE_EDIT);
      setOpenEditTemplateDialog(true);
    } catch (e) {
      handleApiError(e);
    }
  };

  const closeAlertDeleteModal = useCallback(() => {
    dispatch(templateActions.changeMenuSelectId(undefined));
    dispatch(templateActions.changeOpenAlertDelete(false));
  }, [dispatch]);

  const onDeleteSelectedTemplates = useCallback(async () => {
    let payloadDeleteTemplates: IPayloadDeleteTemplates =
      menuSelectedId !== undefined ? { ids: [menuSelectedId] } : { ids: [...selectedIds] };
    try {
      await templateApi.deleteTemplate(payloadDeleteTemplates);
      notify('success', t('deleteSuccessMessage', { ns: 'admin/common' }));
      dispatch(templateActions.changeNeedRefreshData(true));
      dispatch(templateActions.changeOpenAlertDelete(false));
    } catch (e: unknown) {
      catchError(e, (message) => {
        notify('error', message);
      });
    }
  }, [dispatch, menuSelectedId, selectedIds, t]);

  return (
    <Box className='w-full h-full'>
      <Box className='flex flex-row'>
        <BaseHeaderTitle title={t('openingStatementTemplate')} />
        <Box>
          {permissions[PERMISSIONS.openingStatementTemplate_delete] && (
            <Button
              className={cx(
                { 'visibility: visible': selectedIds.length > 0 },
                { 'visibility: hidden': selectedIds.length <= 0 }
              )}
              onClick={openAlertDeleteModal}
              variant='contained'
              color='error'
              disableElevation
              startIcon={<TrashIcon className='w-[17px] h-[17px]' stroke='white' />}
            >
              {t('delete')}
            </Button>
          )}
          {permissions[PERMISSIONS.openingStatementTemplate_create] ? (
            <Button
              className='h-[36px] px-[14px] py-[9.5px] text-[14px]/[16.8px] font-medium rounded-lg ml-[10px]'
              variant='contained'
              disableElevation
              startIcon={<PlusIcon className='w-[10px] h-[10px]' />}
              onClick={handleClickTemplate}
            >
              {t('addTemplate')}
            </Button>
          ) : (
            <></>
          )}
        </Box>
      </Box>

      <Box className='mt-[40px]'>
        {permissions[PERMISSIONS.openingStatementTemplate_view] ? (
          <OpeningStatementList onOpenEdit={handleOpenEdit} />
        ) : null}
      </Box>

      <EditOpeningStatementDialog
        open={openEditTemplateDialog}
        mode={editTemplateDialogMode}
        setOpen={handleSetOpen}
        data={editData}
      />
      <ModalAlertDelete
        open={openAlertDelete}
        description={t('alertDescription')}
        onCancel={closeAlertDeleteModal}
        onConfirm={onDeleteSelectedTemplates}
      />
    </Box>
  );
};

export default TemplateOpeningStatement;
